import { checkIfFileExists } from '@hashnode/file-parsing'
import path from 'path'
import { ZodError } from 'zod'
import { formatZodErrors } from '../../format-zod-error'
import { isNonNullable } from '../../helpers'
import { ParsedSidebarItemConfig } from '../../schemas'
import {
	ConfigError,
	EnhancedGuideInformation,
	EnhancedGuideInformationWithPageContent,
	FileErrorMap,
	FormattedZodError,
	GuideFileErrorMap,
	VersionFileErrorMap,
} from '../../types'
import { walkSidebarItemsWithAdditionalInfo } from '../../walk'
import { generateSlug } from '../generate-slug'
import { parsePageContent } from '../parse/parse-page'

export function checkIfPageExists(filePath: string) {
	return checkIfFileExists(filePath)
}

export function checkPageContent(filePath: string) {
	try {
		return parsePageContent(filePath)
	} catch (error) {
		return null
	}
}

export function validatePageContent(guides: EnhancedGuideInformation[]): {
	contentErrors: GuideFileErrorMap<ConfigError>
	guides: EnhancedGuideInformationWithPageContent[]
} {
	const contentErrors: GuideFileErrorMap<ConfigError> = new Map()

	const result = guides.map<EnhancedGuideInformationWithPageContent>((guide) => {
		const contentErrorsPerVersion: VersionFileErrorMap<ConfigError> = new Map()

		const updatedVersionInformation = guide.versionInformation.map<
			EnhancedGuideInformationWithPageContent['versionInformation'][0]
		>((version) => {
			const contentErrorsPerPage: FileErrorMap<ConfigError> = new Map()
			const existingFiles = version.allPagePaths
				.map((pageFilePath) => {
					try {
						return {
							...parsePageContent(pageFilePath),
							filePath: pageFilePath,
						}
					} catch (error) {
						let message: string | undefined = undefined
						let zodErrors: FormattedZodError[] | undefined = undefined
						if (error instanceof ZodError) {
							zodErrors = formatZodErrors(pageFilePath, error as ZodError)
						} else {
							message = (error as Error).message
						}

						contentErrorsPerPage.set(pageFilePath, [
							{ message: `Page content is not valid${message ? ` ${message}` : ''}`, zodErrors },
						])
						return null
					}
				})
				.filter(isNonNullable)

			contentErrorsPerVersion.set(version.versionFolderPath, contentErrorsPerPage)

			// Make a lookup of all the existing files
			let existingFileLookup = existingFiles.reduce((acc, file) => {
				return {
					...acc,
					[file.filePath]: file,
				}
			}, {})

			// Update the sidebar items with the full slugs
			if ('sidebar' in version.config) {
				walkSidebarItemsWithAdditionalInfo<ParsedSidebarItemConfig, { parentPath: string }>(
					version.config.sidebar,
					{ parentPath: '' },
					(item, { parentPath }) => {
						if (item.type === 'page') {
							const fullPath = path.join(version.versionFolderPath, item.path)

							const page = existingFileLookup[fullPath]
							if (!page) {
								return {
									parentPath,
								}
							}

							const slug = generateSlug({
								title: page.title ?? '',
								slug: page.slug ?? '',
							})

							const newPath = `${parentPath}/${slug}`

							existingFileLookup = {
								...existingFileLookup,
								[fullPath]: {
									...page,
									slug: newPath,
								},
							}

							return {
								parentPath: newPath,
							}
						}

						if (item.type === 'section') {
							const slug = generateSlug({
								title: item.label ?? '',
								slug: item.slug ?? '',
							})

							const newPath = `${parentPath}/${slug}`
							return {
								parentPath: newPath,
							}
						}

						return {
							parentPath,
						}
					},
				)

				return {
					...version,
					existingFiles: Object.keys(existingFileLookup).map((key) => existingFileLookup[key]),
				}
			}

			return {
				...version,
				existingFiles,
			}
		})

		contentErrors.set(guide.guideFolderPath, contentErrorsPerVersion)
		return {
			...guide,
			versionInformation: updatedVersionInformation,
		}
	})

	return {
		contentErrors,
		guides: result,
	}
}
